/**
 * Cross-subdomain session storage for Supabase Auth.
 *
 * Stores the auth session in cookies scoped to `.radiologylive.ai` so a single
 * login is shared by every RadiologyLive web product (portal, Speak, Insight,
 * Rounds). On localhost the Domain attribute is omitted (host-only cookie),
 * which still shares across dev ports since cookies ignore the port.
 *
 * Values larger than one cookie can hold are split into numbered chunks
 * (`<key>.0`, `<key>.1`, …) because browsers cap each cookie at ~4 KB and a
 * Supabase session JSON regularly exceeds that.
 *
 * CANONICAL COPY: radiologylive-ai/src/core/auth/cookie-storage.ts
 * Copies in radiologylive-speak / -insight / -rounds must stay byte-identical.
 * Desktop (Tauri) builds keep localStorage and must NOT use this adapter.
 */

const SHARED_DOMAIN = 'radiologylive.ai';
// Encoded payload per cookie; leaves headroom for name + attributes under 4 KB.
const CHUNK_SIZE = 3000;
const MAX_CHUNKS = 8;
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function cookieDomainAttr(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (host === SHARED_DOMAIN || host.endsWith(`.${SHARED_DOMAIN}`)) {
    return `; Domain=.${SHARED_DOMAIN}`;
  }
  return '';
}

function secureAttr(): string {
  if (typeof window === 'undefined') return '';
  return window.location.protocol === 'https:' ? '; Secure' : '';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return part.slice(prefix.length);
    }
  }
  return null;
}

function writeCookie(name: string, encodedValue: string): void {
  document.cookie =
    `${encodeURIComponent(name)}=${encodedValue}` +
    `; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax` +
    cookieDomainAttr() +
    secureAttr();
}

function deleteCookie(name: string): void {
  document.cookie =
    `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax` +
    cookieDomainAttr() +
    secureAttr();
}

function deleteAllChunks(key: string): void {
  deleteCookie(key);
  for (let i = 0; i < MAX_CHUNKS; i++) {
    if (readCookie(`${key}.${i}`) === null) break;
    deleteCookie(`${key}.${i}`);
  }
}

/**
 * Storage adapter for supabase-js `auth.storage`. Synchronous cookie access
 * wrapped in the (optionally async) interface supabase-js expects.
 */
export const sharedCookieStorage = {
  getItem(key: string): string | null {
    const single = readCookie(key);
    if (single !== null) return decodeURIComponent(single);

    const chunks: string[] = [];
    for (let i = 0; i < MAX_CHUNKS; i++) {
      const chunk = readCookie(`${key}.${i}`);
      if (chunk === null) break;
      chunks.push(chunk);
    }
    if (chunks.length === 0) return null;
    return decodeURIComponent(chunks.join(''));
  },

  setItem(key: string, value: string): void {
    deleteAllChunks(key);
    const encoded = encodeURIComponent(value);
    if (encoded.length <= CHUNK_SIZE) {
      writeCookie(key, encoded);
      return;
    }
    // Chunk boundaries land on the encoded string, never splitting a %XX
    // escape across cookies would corrupt it — so split at offsets that
    // back off below any partial escape sequence.
    let offset = 0;
    let index = 0;
    while (offset < encoded.length && index < MAX_CHUNKS) {
      let end = Math.min(offset + CHUNK_SIZE, encoded.length);
      const tail = encoded.slice(end - 2, end);
      if (tail.includes('%')) {
        end -= tail.lastIndexOf('%') === 1 ? 1 : 2;
      }
      writeCookie(`${key}.${index}`, encoded.slice(offset, end));
      offset = end;
      index += 1;
    }
  },

  removeItem(key: string): void {
    deleteAllChunks(key);
  },
};
